/**
 * Page view and deduped tracking hooks (Milestone 15).
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getPageAttributionContext } from "../utils/pageAttributionContext";
import { trackPageView } from "./analytics";

const firedOnceKeys = new Set();

export function resetAnalyticsDedupeCache() {
  firedOnceKeys.clear();
}

export function shouldTrackOnce(key) {
  if (!key || firedOnceKeys.has(key)) return false;
  firedOnceKeys.add(key);
  return true;
}

/**
 * Track page_view once per pathname + search change.
 */
export function useTrackPageView(extra = {}) {
  const location = useLocation();
  const lastKeyRef = useRef("");

  useEffect(() => {
    const key = `${location.pathname}${location.search}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    const page = getPageAttributionContext(location.pathname);
    const queryParams = Object.fromEntries(new URLSearchParams(location.search));

    trackPageView({
      ...page,
      routeName: location.pathname,
      queryParams: Object.keys(queryParams).length ? queryParams : undefined,
      ...extra,
    });
  }, [location.pathname, location.search, extra]);
}

/**
 * Fire callback once per stable key (e.g. listing detail viewed).
 */
export function useTrackOnce(key, callback, deps = []) {
  const firedRef = useRef(false);

  useEffect(() => {
    if (!key || firedRef.current) return;
    firedRef.current = true;
    try {
      callback();
    } catch {
      // no-op
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ...deps]);
}

/**
 * Impression tracking via IntersectionObserver.
 * TODO(M16+): adopt broadly for card impressions when stable thresholds are defined.
 */
export function useTrackImpression(ref, eventName, payload = {}, { threshold = 0.35, enabled = true } = {}) {
  const firedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !ref?.current || !eventName || firedRef.current) return undefined;
    if (typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= threshold);
        if (!visible || firedRef.current) return;
        firedRef.current = true;
        import("./analytics").then(({ trackEvent }) => trackEvent(eventName, payload));
      },
      { threshold }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, eventName, payload, threshold, enabled]);
}
